import React, { useState, useEffect, useRef } from 'react';
import { View, TextInput, Pressable, StyleSheet, Platform } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../../constants/GoogleMapsConfig';
import LocationSuggestions, { LocationSuggestion } from '../search_box/LocationSuggestions';

interface GooglePrediction {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text?: string;
    };
}

interface Props {
    value: string;
    onChangeText: (text: string) => void;
    onSelectPlace: (placeId: string, description: string) => void;
    placeholder?: string;
    containerStyle?: any;
    inputStyle?: any;
    showNearbyOption?: boolean;
}

const Libraries: Array<'places'> = ['places'];

const AddressAutocomplete: React.FC<Props> = ({
    value,
    onChangeText,
    onSelectPlace,
    placeholder = "Search for address...",
    containerStyle,
    inputStyle,
    showNearbyOption = false,
}) => {
    const [predictions, setPredictions] = useState<GooglePrediction[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_CONFIG.googleMapsApiKey,
        libraries: Libraries,
    });

    const autocompleteService = useRef<any>(null);
    const containerRef = useRef<View>(null);

    useEffect(() => {
        if (isLoaded && (window as any).google && !autocompleteService.current) {
            autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
        }
    }, [isLoaded]);

    const fetchPredictions = (text: string) => {
        if (!text || text.length <= 1) {
            setPredictions([]);
            return;
        }

        if (autocompleteService.current) {
            autocompleteService.current.getPlacePredictions(
                {
                    input: text,
                    componentRestrictions: { country: 'us' }
                },
                (preds: GooglePrediction[] = []) => {
                    setPredictions(preds || []);
                }
            );
        }
    };

    const debounceTimeout = useRef<any>(null);
    const debouncedFetch = (text: string) => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            fetchPredictions(text);
        }, 300);
    };

    // Handle click outside to close suggestions
    useEffect(() => {
        if (Platform.OS !== 'web') return;

        const handleClickOutside = (event: any) => {
            if (containerRef.current && !(containerRef.current as any).contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <View ref={containerRef} style={[styles.container, containerStyle]}>
            <View style={[styles.inputWrapper, inputStyle]}>
                <TextInput
                    style={styles.input}
                    placeholder={placeholder}
                    placeholderTextColor="#94A3B8"
                    value={value}
                    onFocus={() => setShowSuggestions(true)}
                    onChangeText={(text) => {
                        onChangeText(text);
                        setShowSuggestions(true);
                        debouncedFetch(text);
                    }}
                />
                <MaterialCommunityIcons name="map-marker-outline" size={20} color="#64748B" />
            </View>

            <LocationSuggestions
                visible={showSuggestions && (predictions.length > 0 || !value || value.length <= 1)}
                suggestions={predictions.map(p => ({
                    title: p.structured_formatting?.main_text || p.description,
                    subtitle: p.structured_formatting?.secondary_text,
                    value: p.description,
                    icon: 'map-marker',
                    place_id: p.place_id // Pass through place_id
                }))}
                showNearbyOption={showNearbyOption}
                onSelect={(s: any) => {
                    if (s?.isNearby) {
                        // Nearby maps to empty location text in search flows.
                        onChangeText('');
                        setShowSuggestions(false);
                        setPredictions([]);
                        return;
                    }
                    onChangeText(s.value);
                    setShowSuggestions(false);
                    setPredictions([]);
                    onSelectPlace(s.place_id, s.value);
                }}
                style={styles.suggestionOveride}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        position: 'relative',
        zIndex: 10000,
    },
    inputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        paddingHorizontal: 12,
        height: 48,
    },
    input: {
        flex: 1,
        fontSize: 15,
        color: '#1E293B',
        padding: 0,
        height: '100%',
    },
    suggestionOveride: {
        top: 52,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowOpacity: 0.1,
        shadowRadius: 10,
    }
});

export default AddressAutocomplete;
